import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { usePageEntityContext, entityDetailPath } from '@/lib/pageEntityContext';
import { openRelatedRecords } from '@/lib/relatedRecords';
import { relatedViewsByEntity } from '@/config/relatedViews.generated';
import type { SlashCommand } from '@/components/GlobalSearch/commands';

/**
 * The palette's "Show related" rows for the record the current page is showing
 * (Related-Views spec Phase 5). The rows come from the generated registry — the same
 * `ui.relatedViews` declarations that drive the detail view's Related menu — and they
 * always NAVIGATE: a list view lands on the one related record when exactly one is
 * filed under this record, else on the target's grid pre-filtered on it (the
 * entityListPath contract, chip included); a detail view lands on the single related
 * record. The slide-out panel stays a detail-view-local affordance.
 */
/**
 * The open record's detail-cache key, matched by shape, as useContextCommands does: the
 * generated factories produce `[plural, tenantId, 'detail', id]` and the plural is not
 * derivable from the entity name.
 */
function isRecordDetailKey(queryKey: QueryKey, entityId: string): boolean {
  return queryKey.includes('detail') && queryKey[queryKey.length - 1] === entityId;
}

function readCachedRecord(queryClient: QueryClient, entityId: string | undefined) {
  if (!entityId) return undefined;
  const cached = queryClient.getQueriesData<Record<string, unknown>>({
    predicate: (q) => isRecordDetailKey(q.queryKey, entityId),
  });
  for (const [, data] of cached) {
    if (data) return data;
  }
  return undefined;
}

export function useRelatedCommands(): SlashCommand[] {
  const { entityType, entityId, displayField } = usePageEntityContext();
  const queryClient = useQueryClient();

  /**
   * The open record, read from the cache the detail view fills. It supplies the filter
   * chip's label and, for detail rows, the target record's id. Subscribed, not read once:
   * the palette stays mounted across navigation, so it sees the route change before the
   * detail fetch lands. Until then the label is missing and detail rows are dropped
   * rather than emitted dead.
   */
  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        if (entityId && isRecordDetailKey(event.query.queryKey, entityId)) onChange();
      }),
    [queryClient, entityId],
  );
  const record = useSyncExternalStore(subscribe, () => readCachedRecord(queryClient, entityId));

  return useMemo(() => {
    if (!entityType || !entityId) return [];
    const entries = relatedViewsByEntity[entityType];
    if (!entries || entries.length === 0) return [];

    const labelValue = displayField ? record?.[displayField] : undefined;
    const recordLabel = typeof labelValue === 'string' && labelValue ? labelValue : undefined;

    const built: SlashCommand[] = [];
    for (const entry of entries) {
      const base = {
        id: `related.${entityType}.${entry.key}`,
        title: { key: entry.labelKey, value: entry.defaultLabel },
        subtitle: { key: `DisplayName:${entry.entity}`, value: entry.entity },
        group: 'related' as const,
        icon: entry.icon,
        permission: entry.permission,
      };
      if (entry.kind === 'detail') {
        const target = entry.sourceField ? record?.[entry.sourceField] : undefined;
        if (target == null || target === '') continue;
        built.push({
          ...base,
          run: ({ navigate, closeDialog }) => {
            closeDialog();
            navigate(entityDetailPath(entry.module, entry.entity, String(target)));
          },
        });
      } else {
        built.push({
          ...base,
          // The one related record itself when there is exactly one, else the filtered list.
          run: ({ navigate, closeDialog }) => {
            closeDialog();
            void openRelatedRecords(navigate, {
              apiPath: entry.apiPath!,
              module: entry.module,
              entity: entry.entity,
              field: entry.filterField!,
              value: entityId,
              label: recordLabel,
            });
          },
        });
      }
    }
    return built;
  }, [entityType, entityId, displayField, record]);
}
